import { useCallback, useEffect } from 'react';
import { Row, Col } from 'src/componentes/grid';
import ViewDocument from 'src/componentes/arquivo-upload/ViewDocument';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import Input from 'src/componentes/input';
import Radio from 'src/componentes/radio';
import {
  COPIA_CONTROLADA_FIELDS,
  POSICAO_MARCA_DAGUA,
  TIPO_COPIA_DOCUMENTO,
  TIPO_MARCA_DAGUA,
  posicoesMarcaDagua,
  tipoCopiaDocumentoHints,
  tipoCopiaDocumentoOptions,
} from './copiaControladaDocumentoConstants';
import { Eye } from 'lucide-react';
import Tooltip from 'src/componentes/tooltip';

const obterNomeArquivo = (arquivo) => arquivo?.nome;

const podeVisualizarArquivo = (arquivo) => {
  const nomeArquivo = obterNomeArquivo(arquivo);

  return (
    typeof nomeArquivo === 'string' &&
    (nomeArquivo.toLowerCase().endsWith('.pdf') ||
      nomeArquivo.toLowerCase().endsWith('.docx') ||
      nomeArquivo.toLowerCase().endsWith('.doc'))
  );
};

function CopiaControladaDocumentoSection({
  intl,
  documentoId,
  isDisabled,
  getValue,
  onChangeValue,
  hasErrorField = () => false,
  hasUnsavedChanges = false,
}) {
  const getConfiguracaoValue = useCallback(
    (fieldName, fallbackFieldName) => {
      const value = getValue(fieldName);

      if (value !== undefined) return value;
      if (!fallbackFieldName) return value;

      return getValue(fallbackFieldName);
    },
    [getValue]
  );

  const arquivos = Array.isArray(getValue('arquivos'))
    ? getValue('arquivos')
    : [];
  const arquivoPreview = arquivos.find(podeVisualizarArquivo);
  const tipoCopiaDocumento =
    getConfiguracaoValue(
      COPIA_CONTROLADA_FIELDS.TIPO_COPIA_DOCUMENTO_ID,
      'tipoCopiaDocumento'
    ) ?? TIPO_COPIA_DOCUMENTO.NAO_SE_APLICA;
  const posicaoMarcaDagua =
    getConfiguracaoValue(
      COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID,
      'posicaoMarcaDagua'
    ) ?? POSICAO_MARCA_DAGUA.CENTRO;
  const isCopiaControlada =
    tipoCopiaDocumento === TIPO_COPIA_DOCUMENTO.COPIA_CONTROLADA;
  const permiteConfigurarTextoCopia =
    tipoCopiaDocumento === TIPO_COPIA_DOCUMENTO.COPIA_CONTROLADA ||
    tipoCopiaDocumento === TIPO_COPIA_DOCUMENTO.COPIA_NAO_CONTROLADA;
  const isPreviewDisabled = hasUnsavedChanges || !arquivoPreview;
  const previewTooltip = hasUnsavedChanges
    ? intl.formatMessage({ id: 'tooltipSalvarAlteracoesPreviewDocumento' })
    : intl.formatMessage({ id: 'visualizarDocumento' });
  const tipoCopiaDocumentoHintItens = tipoCopiaDocumentoHints.map((hint) => ({
    ...hint,
    label: intl.formatMessage({ id: hint.label }),
  }));

  const handlePreviewDocumento = () => {
    const nomeArquivo = obterNomeArquivo(arquivoPreview);
    if (!nomeArquivo) return;

    ContentManager.addContent(
      <ViewDocument
        fileName={nomeArquivo}
        onlyRead
        convertWordToPdf
        urlCustom="DocumentoDownload/DownloadFile"
        documentName={getValue('nome')}
        documentType={getValue('tipoDocumento')?.nome}
        parameters={{
          documentoId,
          isDownload: false,
          previewMarcaDagua: true,
          copiaControlada: false,
        }}
      />
    );
  };

  const clearConfiguracaoCopiaControlada = useCallback(() => {
    onChangeValue(COPIA_CONTROLADA_FIELDS.TIPO_MARCA_DAGUA_ID, null);
    onChangeValue(COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID, null);
    onChangeValue(COPIA_CONTROLADA_FIELDS.TEXTO_MARCA_DAGUA, null);
    onChangeValue(COPIA_CONTROLADA_FIELDS.ARQUIVO_MARCA_DAGUA, null);
  }, [onChangeValue]);

  const handleChangeTipoCopia = (value) => {
    onChangeValue(COPIA_CONTROLADA_FIELDS.TIPO_COPIA_DOCUMENTO_ID, value);

    if (value === TIPO_COPIA_DOCUMENTO.NAO_SE_APLICA) {
      clearConfiguracaoCopiaControlada();
      return;
    }

    onChangeValue(
      COPIA_CONTROLADA_FIELDS.TIPO_MARCA_DAGUA_ID,
      TIPO_MARCA_DAGUA.TEXTO
    );

    if (
      getConfiguracaoValue(
        COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID,
        'posicaoMarcaDagua'
      ) == null
    ) {
      onChangeValue(
        COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID,
        POSICAO_MARCA_DAGUA.CENTRO
      );
    }
  };

  useEffect(() => {
    if (!permiteConfigurarTextoCopia) return;

    if (
      getConfiguracaoValue(
        COPIA_CONTROLADA_FIELDS.TIPO_MARCA_DAGUA_ID,
        'tipoMarcaDagua'
      ) !== TIPO_MARCA_DAGUA.TEXTO
    ) {
      onChangeValue(
        COPIA_CONTROLADA_FIELDS.TIPO_MARCA_DAGUA_ID,
        TIPO_MARCA_DAGUA.TEXTO
      );
    }

    if (
      getConfiguracaoValue(
        COPIA_CONTROLADA_FIELDS.ARQUIVO_MARCA_DAGUA,
        'arquivoMarcaDagua'
      ) != null
    ) {
      onChangeValue(COPIA_CONTROLADA_FIELDS.ARQUIVO_MARCA_DAGUA, null);
    }

    if (
      getConfiguracaoValue(
        COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID,
        'posicaoMarcaDagua'
      ) == null
    ) {
      onChangeValue(
        COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID,
        POSICAO_MARCA_DAGUA.CENTRO
      );
    }
  }, [getConfiguracaoValue, onChangeValue, permiteConfigurarTextoCopia]);

  const getPosicaoTooltipLabelId = (posicaoId) => {
    const map = {
      [POSICAO_MARCA_DAGUA.SUPERIOR_CENTRO]: 'labelTopo',
      [POSICAO_MARCA_DAGUA.CENTRO_ESQUERDA]: 'labelLateralEsquerda',
      [POSICAO_MARCA_DAGUA.CENTRO]: 'labelCentroDiagonal',
      [POSICAO_MARCA_DAGUA.CENTRO_DIREITA]: 'labelLateralDireita',
      [POSICAO_MARCA_DAGUA.INFERIOR_CENTRO]: 'labelRodape',
    };

    return map[posicaoId] || 'labelPosicaoMarcaDagua';
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <Radio
            disabled={isDisabled}
            needId
            name={COPIA_CONTROLADA_FIELDS.TIPO_COPIA_DOCUMENTO_ID}
            options={tipoCopiaDocumentoOptions}
            hintItens={tipoCopiaDocumentoHintItens}
            labelFunction={(item) => intl.formatMessage({ id: item.label })}
            model={{
              value: tipoCopiaDocumento,
              requestChange: handleChangeTipoCopia,
            }}
          />
        </Col>
      </Row>
      {permiteConfigurarTextoCopia && (
        <>
          {isCopiaControlada && (
            <Row>
              <Col md={4}>
                <Input
                  type="number"
                  min={1}
                  disabled={isDisabled}
                  required
                  model={{
                    label: intl.formatMessage({
                      id: 'labelQuantidadePermitidaCopias',
                    }),
                    value: getConfiguracaoValue(
                      COPIA_CONTROLADA_FIELDS.QUANTIDADE_PERMITIDA_COPIAS,
                      'quantidadePermitidaCopias'
                    ),
                    errors: hasErrorField(
                      COPIA_CONTROLADA_FIELDS.QUANTIDADE_PERMITIDA_COPIAS
                    ),
                    requestChange: (value) =>
                      onChangeValue(
                        COPIA_CONTROLADA_FIELDS.QUANTIDADE_PERMITIDA_COPIAS,
                        value === '' ? null : Number(value)
                      ),
                  }}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col md={12}>
              <label className="control-label mb-4">
                {intl.formatMessage({ id: 'labelPosicaoMarcaDagua' })} *
              </label>
              <div
                style={{
                  width: 159,
                  height: 216,
                  border: '1px solid #d8dde3',
                  borderRadius: 8,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gridTemplateRows: 'repeat(3, 1fr)',
                  columnGap: 20,
                  rowGap: 50,
                  alignItems: 'center',
                  justifyItems: 'center',
                  background: '#fff',
                  backgroundImage: 'repeating-linear-gradient(to bottom, #F4F4F5 0px, #F4F4F5 4px, transparent 4px, transparent 12px)',
                  backgroundSize: 'calc(100% - 24px) calc(100% - 24px)',
                  backgroundPosition: '12px 12px',
                  backgroundRepeat: 'no-repeat',
                }}
              >
                {posicoesMarcaDagua.map((posicao) => {
                  const tooltipLabelId = getPosicaoTooltipLabelId(posicao.id);

                  return (
                    <Tooltip
                      key={posicao.id}
                      text={intl.formatMessage({ id: tooltipLabelId })}
                      placement="bottom"
                    >
                    <span
                      style={{
                        gridColumn: posicao.gridColumn,
                        gridRow: posicao.gridRow,
                        display: 'inline-flex',
                      }}
                    >
                      <button
                        type="button"
                        disabled={isDisabled}
                        aria-label={intl.formatMessage({
                          id: tooltipLabelId,
                        })}
                        onClick={() =>
                          onChangeValue(
                            COPIA_CONTROLADA_FIELDS.POSICAO_MARCA_DAGUA_ID,
                            posicao.id
                          )
                        }
                        style={{
                          width: posicao.buttonWidth,
                          height: posicao.buttonHeight,
                          borderRadius: 4,
                          border: 0,
                          background: posicaoMarcaDagua === posicao.id ? 'var(--primary)' : '#D4D4D8',
                          padding: 0,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          transform:
                            posicao.id === POSICAO_MARCA_DAGUA.CENTRO ? 'rotate(-45deg)' : 'none',
                          transformOrigin: 'center',
                        }}
                      />
                    </span>
                    </Tooltip>
                  );
                })}
              </div>
            </Col>
          </Row>
          <Row className="my-3">
            <Col md={12}>
              {permiteConfigurarTextoCopia && (
                <span title={previewTooltip}>
                  <Button
                    color="link"
                    className="p-0 text-primary font-weight-bolder text"
                    disabled={isPreviewDisabled}
                    onClick={handlePreviewDocumento}
                    title={previewTooltip}
                  >
                    <Eye size={15} className="mr-2" />
                    {intl.formatMessage({ id: 'visualizarDocumento' })}
                  </Button>
                </span>
              )}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={12}>
              <Input
                type="textarea"
                rows={3}
                disabled={isDisabled}
                model={{
                  label: intl.formatMessage({
                    id: 'labelTextoApoioMarcaDagua',
                  }),
                  value: getConfiguracaoValue(
                    COPIA_CONTROLADA_FIELDS.TEXTO_MARCA_DAGUA,
                    'textoMarcaDagua'
                  ),
                  requestChange: (value) =>
                    onChangeValue(
                      COPIA_CONTROLADA_FIELDS.TEXTO_MARCA_DAGUA,
                      value
                    ),
                }}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

export default CopiaControladaDocumentoSection;
